import logging from "utils/logging";
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { selectQuestionById, submitAnswerAction } from "./questionSlice";
import { selectCurrentSubmission } from "./submissionSlice";
import { selectGameStatusObject } from "../gamestate/gameStateSlice";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";
import {
  GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING,
  GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
  GAMESTATE_ROUNDSTATE_QUESTION_READING,
  GAMESTATE_ROUNDSTATE_QUESTION_RESULTS,
  SUBMISSION_STATE_COMMITTED,
  SUBMISSION_STATE_CORRECT,
} from "../gamestate/constants";
import AnswerSubmitButton from "./components/AnswerSubmitButton";
import AnswerTextField from "./components/AnswerTextField";
import QuestionCountdown from "../questionCountdown/QuestionCountdown";
import * as Constants from "../gamestate/constants";
import { updateGameStateAction } from "../gamestate/stateActions";

const log = logging.getLogger("GameComponent");

const Root = styled(Container)(({ theme }) => ({
  backgroundColor: '#fff',
  color: '#222',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

const QuestionHeader = styled(Typography)({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "2em",
  color: '#2ec4b6',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  textShadow: '2px 2px 4px rgba(46, 196, 182, 0.3)',
  borderBottom: '3px solid #2ec4b6',
  paddingBottom: '8px',
  marginBottom: '16px',
  textAlign: 'center',
});

const QuestionValue = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.8em",
  color: '#e7ae02',
  fontWeight: 700,
  textAlign: 'center',
  marginTop: theme.spacing(1),
}));

const Clue = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.5em",
  color: '#444',
  fontWeight: 500,
  marginTop: theme.spacing(2),
  lineHeight: 1.5,
  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
}));

const Result = styled(Typography)({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.2em",
  fontWeight: 600,
});

const PlusPoints = styled('span')({
  color: "#2ec4b6",
});

const MinusPoints = styled('span')({
  color: "#e74c3c",
});

const CorrectAnswer = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontFamily: "'Poppins', 'Inter', sans-serif",
  fontSize: "1.1em",
  color: '#222',
  backgroundColor: '#f8f9fa',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  borderLeft: `4px solid #2ec4b6`,
}));

const CountdownContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(3),
}));

const Prompt = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', 'Inter', sans-serif",
  paddingTop: theme.spacing(2),
  fontSize: "1.3em",
  color: '#2ec4b6',
  fontWeight: 500,
}));

const Question = ({ gameId, userId, playerId, questionId }) => {
  const dispatch = useDispatch();
  const answeringStartedAt = useRef(null);
  const answerInputRef = useRef(null);

  // ---- Local state items
  const [answer, setAnswer] = useState("");

  // ---- Selectors from global redux state
  const questionObject = useSelector((state) =>
    selectQuestionById(state, questionId)
  );
  const submissionObject = useSelector(selectCurrentSubmission);
  const gameState = useSelector(selectGameStatusObject);

  // iOS Safari does not honor autoFocus reliably on programmatic remounts —
  // explicitly focus the answer input when the round transitions to answering
  // so mobile players don't have to tap into the field.
  const hasSubmitted = !!submissionObject?.answer;
  useEffect(() => {
    if (
      gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING &&
      !hasSubmitted
    ) {
      const id = setTimeout(() => answerInputRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
  }, [gameState.roundState, hasSubmitted]);

  // Track when the answering phase begins so we can measure time_to_submit
  if (
    gameState.roundState === GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING &&
    !answeringStartedAt.current
  ) {
    answeringStartedAt.current = Date.now();
  } else if (gameState.roundState !== GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING) {
    answeringStartedAt.current = null;
  }

  // ---- Local functions/actions
  const onSubmit = () => {
    log.info("Submitting answer: ", answer);

    const timeToSubmitS = answeringStartedAt.current
      ? (Date.now() - answeringStartedAt.current) / 1000
      : undefined;

    Analytics.track(EVENTS.ANSWER_SUBMITTED, {
      game_id: gameId,
      question_id: questionId,
      round: gameState.round,
      category: questionObject?.category,
      point_value: questionObject?.value || 0,
      answer_length: answer.length,
      time_to_submit_s: timeToSubmitS,
      is_daily_double: !!questionObject?.dailyDouble,
    });

    // Maybe make this a big ole thunk action
    dispatch(submitAnswerAction(gameId, questionId, playerId, answer));

    if (questionObject.dailyDouble) {
      // It's a daily double, so we can also advance the state to judging
      const { endTime, ...rest } = gameState;
      const newState = {
        ...rest,
        roundState: Constants.GAMESTATE_ROUNDSTATE_QUESTION_JUDGING,
      };
      dispatch(updateGameStateAction(gameId, newState));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && answer.length > 0) {
      onSubmit();
    }
  };

  // ---- Render the outputs

  if (!questionObject) {
    return <div>Loading...</div>;
  }

  let responseArea = <div />;
  let button = <div />;
  let extra = <div />;

  let prompt = questionObject.prompt ? (
    <Prompt>{questionObject.prompt}</Prompt>
  ) : (
    <div />
  );

  switch (gameState.roundState) {
    case GAMESTATE_ROUNDSTATE_QUESTION_READING:
      prompt = <div />;
      break;
    case GAMESTATE_ROUNDSTATE_QUESTION_ANSWERING:
      if (submissionObject && submissionObject.answer) {
        responseArea = (
          <AnswerTextField
            disabled={true}
            label="My Answer"
            value={submissionObject.answer}
          />
        );
        button = (
          <AnswerSubmitButton
            completed={true}
            disabled={true}
            text={"Submitted"}
          />
        );
      } else {
        responseArea = (
          <AnswerTextField
            autoFocus
            inputRef={answerInputRef}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={handleKeyDown}
            label={"Answer"}
          />
        );
        const fieldEmpty = answer.length === 0;
        button = (
          <AnswerSubmitButton
            disabled={fieldEmpty}
            text={"Submit"}
            onClick={onSubmit}
          />
        );
      }
      extra = (
        <CountdownContainer>
          <QuestionCountdown
            endTimeStr={questionObject.endTime}
            startTimeStr={questionObject.startTime}
            roundState={gameState.roundState}
            shouldPlayFinalMusic={false}
          />
        </CountdownContainer>
      );

      break;
    case GAMESTATE_ROUNDSTATE_QUESTION_JUDGING:
      responseArea = (
        <AnswerTextField
          disabled={true}
          label="My Answer"
          value={submissionObject ? submissionObject.answer : ""}
        />
      );
      break;
    case GAMESTATE_ROUNDSTATE_QUESTION_RESULTS:
      if (submissionObject) {
        responseArea = (
          <AnswerTextField
            disabled={true}
            label="My Answer"
            value={submissionObject ? submissionObject.answer : ""}
          />
        );

        if (
          submissionObject.status === SUBMISSION_STATE_CORRECT ||
          (submissionObject.status === SUBMISSION_STATE_COMMITTED &&
            submissionObject.points > 0)
        ) {
          button = (
            <Result>
              <PlusPoints>+{submissionObject.points}</PlusPoints>
            </Result>
          );
        } else {
          button = (
            <Result>
              <MinusPoints>-{Math.abs(submissionObject.points)}</MinusPoints>
            </Result>
          );
        }

        if (questionObject.answer) {
          extra = (
            <CorrectAnswer>
              Correct Answer: {questionObject.answer}
            </CorrectAnswer>
          );
        }
      }
      break;
    default:
      break;
  }

  let questionValue = questionObject.value
    ? questionObject.value.toLocaleString()
    : "0";

  if (submissionObject && submissionObject.wager) {
    log.debug("Submission object: ", submissionObject, questionObject);
    questionValue = submissionObject.wager.toLocaleString();
  }

  return (
    <Root>
      <QuestionHeader variant="h5">{questionObject.category}</QuestionHeader>
      <QuestionValue>${questionValue}</QuestionValue>
      <Clue variant="h4">{questionObject.clue.toUpperCase()}</Clue>
      {prompt}
      {responseArea}
      {button}
      {extra}
    </Root>
  );
};

export default Question;
